import React, { useCallback, useState, useEffect } from 'react';
import IWizardStep from '../../../../interfaces/IWizardStep';
import WizardButton from '../../components/WizardButton';
import { WizardContent } from '../../components/WizardContent';
import { WizardSecodaryTitle } from '../../components/WizardSecodaryTitle';
import { WizardTitle } from '../../components/WizardTitle';
import DeleteIcon from '@mui/icons-material/Delete';

import {
  InputFile,
  InputArea,
  OptionPill,
  InputHint,
  CameraIcon,
  FileIcon,
  FileList,
  FileRow,
  FileIconBox,
  FileInfo,
  FileName,
  FileMeta,
  ProgressTrack,
  ProgressBar,
} from './MedicalCertificateImage.styles';

import { useWizardRequest } from '../../../../hooks/services/useWizardRequest';
import { useToastrVanila } from '../../../../hooks/util/useToastrVanila';
import { ShowError, StyledAlert } from '../../components/ErrorMessage';
import { IconButton, CircularProgress } from '@mui/material';
import { IAttachment } from '../../../../interfaces/IAttachment';
import imageCompression from 'browser-image-compression';
import ITypeDocument from '../../../../interfaces/ITypeDocument';
import IMedicalCertificate from '../../../../interfaces/IMedicalCertificate';
import BackupIcon from '@mui/icons-material/Backup';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';

interface MedicalCertificateImageProps extends IWizardStep { }

interface IAttachments {
  id: string;
  medicalCertificateId: string;
  attachment: string;
}

const isPdf = (name: string) => /\.pdf$/i.test(name);

const MedicalCertificateImage = (props: MedicalCertificateImageProps) => {
  const [loading, setLoading] = useState(false);
  const [isMobile] = useState(() => {
    const toMatch: RegExp[] = [/Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i, /BlackBerry/i, /Windows Phone/i];
    return toMatch.some(rx => navigator.userAgent.match(rx));
  });
  const [uploading, setUploading] = useState<{ name: string; size: number } | null>(null);

  const toastr = useToastrVanila();
  const [isError, setIsError] = useState(false);
  const [typeDocument] = useState<ITypeDocument | null>(() => {
    const type = localStorage.getItem('documentType') || null;
    return type ? JSON.parse(type) : null;
  });
  const [attachments, setAttachments] = useState<IAttachments[]>(() => {
    const resource = localStorage.getItem('medicalCertificate');
    if (resource) {
      const medicalCertificate: IMedicalCertificate = JSON.parse(resource);
      return medicalCertificate.attachments || [];
    }
    return [];
  });

  const request = useWizardRequest();

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.item(0);
    if (file) sendFile(file as File);
    e.target.value = ''; 
  };

  const sendFile = async (fileParam: File | null) => {
    if (!fileParam) return;
    setLoading(true);

    if (fileParam.size > 20971520) {
      setLoading(false);
      toastr.error('Arquivo muito grande. Use uma foto ou arquivo menor.');
      return;
    }

    let file = fileParam;

    if (fileParam.type !== 'application/pdf' && fileParam.size > 1048576) {
      try {
        file = await imageCompression(fileParam, {
          maxSizeMB: 1,
          maxWidthOrHeight: 1600,
          useWebWorker: true,
        });
      } catch {
        setLoading(false);
        toastr.error('Não foi possível processar a imagem. Tente uma foto menor.');
        return;
      }
    }

    if (file.size > 5242880) {
      setLoading(false);
      toastr.error('O tamanho máximo do arquivo é 5MB');
      return;
    }

    const data = new FormData();
    data.append('attachment', file, fileParam.name);

    setUploading({ name: fileParam.name, size: file.size });

    await request
      .sendAttachment(`${props.medicalCertificate.id}`, data)
      .then(async () => {
        setLoading(false);
        setIsError(false);
        await props.setStep(props.step, { ...props.medicalCertificate }, props.step - 1).then(() => {
          const resource = localStorage.getItem('medicalCertificate');
          if (resource) {
            const medicalCertificate: IMedicalCertificate = JSON.parse(resource);
            setAttachments(medicalCertificate.attachments || []);
          }
        });
      })
      .catch(() => {
        setLoading(false);
        setUploading(null);
        toastr.error('Ocorreu um erro ao enviar a imagem. Tente novamente mais tarde.');
      });
  };

  useEffect(() => {
    setUploading(null);
  }, [attachments]);

  const removeAttachment = async (attachmentId: string) => {
    setLoading(true);
    try {
      await request.removeAttachment(attachmentId);
      setIsError(false);
      setAttachments(prev => prev.filter(file => file.id !== attachmentId));
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setAttachments(prev => prev.filter(file => file.id !== attachmentId));
      } else {
        toastr.error('Ocorreu um erro ao excluir a imagem. Tente novamente mais tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    if (!attachments || attachments.length === 0) {
      setIsError(true);
      setLoading(false);
      return;
    }
    await props.setStep(props.step + Number(typeDocument?.allowComments ? 1 : 2), { ...props.medicalCertificate });
  }, [attachments]);

  const getFilename = (attachment: IAttachment) => {
    const url = attachment.attachment;
    const fileName = url.split('/')[url.split('/').length - 1];
    return fileName.substring(33, 999);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <WizardContent style={{ position: 'relative' }}>
      <BackupIcon fontSize="large" sx={{ color: '#0F6E56' }} />
      <WizardTitle marginBotton={2}>Envio do atestado</WizardTitle>
      <WizardSecodaryTitle>(Selecione um arquivo ou tire uma foto)</WizardSecodaryTitle>

      {typeDocument && typeDocument.additionalText && typeDocument.additionalText.length > 5 && (
        <div style={{ marginTop: '20px' }}>
          <ShowError show={true}>
            <StyledAlert severity="warning">{typeDocument.additionalText}</StyledAlert>
          </ShowError>
        </div>
      )}

      <form>
        {/* ── BLOCO 1: seletor (visual) ── */}
        <InputArea data-desktop={!isMobile ? 'true' : 'false'}>
          {isMobile && (
            <OptionPill htmlFor="mc-camera-input">
              <CameraIcon />
              Tirar foto
            </OptionPill>
          )}

          <OptionPill htmlFor="mc-file-input">
            <FileIcon />
            Escolher arquivo
          </OptionPill>
        </InputArea>

        <InputHint>PDF, JPG ou PNG · até 5 MB</InputHint>

        <InputFile id="mc-camera-input" type="file" accept="image/*" capture="environment" onChange={onFileChange} />
        <InputFile id="mc-file-input" type="file" accept="application/pdf,image/*" onChange={onFileChange} />

        {(attachments.length > 0 || uploading) && (
          <FileList>
            {attachments.map(a => {
              const name = getFilename(a as unknown as IAttachment);
              return (
                <FileRow key={a.id}>
                  <FileIconBox>
                    {isPdf(name) ? <PictureAsPdfOutlinedIcon fontSize="small" /> : <ImageOutlinedIcon fontSize="small" />}
                  </FileIconBox>
                  <FileInfo>
                    <FileName href={a.attachment} target="_blank" rel="noopener noreferrer" title={name}>
                      {name}
                    </FileName>
                    <FileMeta>Enviado</FileMeta>
                  </FileInfo>
                  <IconButton
                    onClick={() => removeAttachment(a.id)}
                    disabled={loading}
                    sx={{ color: '#9CA3AF', flexShrink: 0 }}
                  >
                    <DeleteIcon sx={{color: '#f80000'}}/>
                  </IconButton>
                </FileRow>
              );
            })}

            {uploading && (
              <FileRow>
                <FileIconBox>
                  {isPdf(uploading.name) ? <PictureAsPdfOutlinedIcon fontSize="small" /> : <ImageOutlinedIcon fontSize="small" />}
                </FileIconBox>
                <FileInfo>
                  <FileName as="span" title={uploading.name}>
                    {uploading.name}
                  </FileName>
                  <FileMeta>Enviando… {formatSize(uploading.size)}</FileMeta>
                  <ProgressTrack>
                    <ProgressBar />
                  </ProgressTrack>
                </FileInfo>
                <CircularProgress size={18} sx={{ color: '#0F6E56', flexShrink: 0 }} />
              </FileRow>
            )}
          </FileList>
        )}

        <div style={{ marginTop: '20px' }}>
          <ShowError show={isError}>
            <StyledAlert severity="error">
              Por favor, adicione a imagem do documento antes de avançar.
            </StyledAlert>
          </ShowError>
        </div>

        <WizardButton isLoading={loading} disabled={loading} sx={{ marginTop: '40px' }} onClick={handleSubmit}>
          PRÓXIMO
        </WizardButton>
      </form>
    </WizardContent>
  );
};

export default MedicalCertificateImage;